import React, {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {
    Box,
    Dialog,
    IconButton,
    Button,
    Slide,
    Theme,
    Toolbar,
    Paper,
    Typography,
    Card,
    CardContent,
    CardHeader,
    CardActions,
    LinearProgress,
    Stack,
    Grid,
    TextField,
    Tabs,
    Tab,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    Tooltip,
    InputAdornment
} from "@mui/material";
import {CloseRounded} from "@mui/icons-material";
import {useTheme} from '@mui/material/styles';
import {Link, useParams, useSearchParams, useLocation} from "react-router-dom";
import {ContainerClasses} from '../../../themes/containerClasses'
import MonitorIcon from '@mui/icons-material/Monitor';
import {getAppName,
        getObjectName} from "../../../utils/helperRouter";
import { useNavigate } from "react-router";
import { useAuth } from "../../../hooks/useAuth";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp'
import {GlobalSearchResultModel} from '../../../models/modelsGlobalSearch';
import { useSnackbar } from "notistack";
import { globalSearchRecords } from "../../../services/backendApi";
import {SearchFieldComponent} from "../../../components/searchField/SearchField";



export const HighlightedText: React.FC<{
  text: string,
  highlight: string
}> = ( {
  text,
  highlight
}) => {
    //return a highlighted text if the highlight string is found
    const regex = new RegExp(`(${highlight.trim()})`, "gi");
    const parts = text.split(regex)

    return (
      <Typography component="span">
        {parts.map((part,index) =>
          regex.test(part) ?
            (
              <Box
                key={index}
                component="span"
                sx={{
                  backgroundColor: 'highlight.main'
                }}
              >
                {part}
              </Box>
            ) :
            (
          <span key={index}>{part}</span>
          )
      )}

      </Typography>
    )

};



export const SearchScreen = () => {

  let {appName} = useParams();

  const theme = useTheme();
  const styles = ContainerClasses();
  const {accessToken, onInvalidToken} = useAuth();

  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  //search query via search parameter
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q')

  const [searchResults, setSearchResults] = useState<GlobalSearchResultModel[]>([]);

  const [filteredObjects, setFilteredObjects] = useState<string[]>([]);

  const [searchText, setSearchText] = useState<string>('');
  const [mutableSearchText, setMutableSearchText] = useState<string>('');


  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if(searchQuery){
      //refetch search from search submitted search parameter
      handleDatabaseSearch(searchQuery)
    }

}, [searchQuery])


  const handleDatabaseSearch = (searchText: string) => {
    const thisSearchText = searchText.trim();

    setIsLoading(true)

    globalSearchRecords(accessToken, thisSearchText)
       .then((results) => {
           setSearchResults(results);
           const allObjects = results.map((thisResult) => thisResult.objectMetadata.Uuid_sys)
           setFilteredObjects(allObjects)
           setSearchText(thisSearchText)
           setIsLoading(false)
         })
         .catch((err) => {
           setIsLoading(false)
           console.log(err)
         })

  }
  const submitDatabaseSearch = (event: React.FormEvent) => {
    event.preventDefault();
    handleDatabaseSearch(mutableSearchText);
  }



  const handleSelectAll = () => {
    if(filteredObjects.length !== searchResults.length){
      //select all
      const allObjects = searchResults.map((thisResult) => thisResult.objectMetadata.Uuid_sys)
      setFilteredObjects(allObjects)
    }
    //otherwise: nothing to be done
    //since already everything selected
  }
  const handleSelectObject = (objectUuid: string) => {
    if(!filteredObjects.includes(objectUuid)){
      setFilteredObjects(prev => [...prev, objectUuid])
    } else {
      //deselect object
      setFilteredObjects(prev => prev.filter((thisUuid) => thisUuid != objectUuid))
    }
  }
  const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    //need to stop clicking "button" which is the complete row
    //when clicking a link
    event.stopPropagation();
  }


  /**********************************/


  const searchResultRecord = (thisRecord: any) => {
    //display a found record as a card
    //in a grid item
    const appApiName = getAppName(appName)
    const link_to = `/${appApiName}/${thisRecord.objectMetadata.ApiName_sys}/${thisRecord.Uuid_sys}`;
    const ignoreFields = ['Id_sys', 'Uuid_sys'];

    return (
      <Grid
        key={`searchRecord:${thisRecord.Uuid_sys}`}
        item
        xs={12}
        sm={6}
        md={6}
      >
        <Card>
          <CardContent>

            <Typography style={{fontWeight: 'bold'}}>
              <Link to={link_to} onClick={handleLinkClick}>
                {thisRecord.LookupDisplayValue_sys}
              </Link>
            </Typography>

            {thisRecord.fields.map((thisField: any) => {
              if(!ignoreFields.includes(thisField.fieldName)){
                 return (
                   <Box
                     key={`${thisField.fieldName}:{thisRecord.Uuid_sys}`}
                     sx={{
                       width:'100%',
                       display: 'flex',
                       alignItems: 'flex-start',
                       gap: 2
                     }}
                   >
                     <Typography
                       fontWeight='700'
                       color='text.secondary'
                      >
                       {thisField.fieldLabel}:
                     </Typography>
                     {
                     <HighlightedText
                        text={thisField.fieldValue}
                        highlight={searchText}
                     />
                     }
                   </Box>
                 )
               }
             })}

          </CardContent>
        </Card>
      </Grid>
    )
  }

  const searchResultAccordion = (thisResult: GlobalSearchResultModel) => {
    //complete accordion for a specific object that has been found
    return (
      <Accordion
         key={`boxObjectUuid:${thisResult.objectMetadata.Uuid_sys}`}
         expanded={filteredObjects.includes(thisResult.objectMetadata.Uuid_sys)}
         onClick={() => handleSelectObject(thisResult.objectMetadata.Uuid_sys)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
           <Typography style={{fontWeight: 'bold'}}>
              {thisResult.objectMetadata.Label_sys} ({thisResult.records.length})
           </Typography>
        </AccordionSummary>

        <AccordionDetails>
          <Grid
            container
            spacing={2}
            sx={{pb: 2}}
          >
          {
            thisResult.records.map((thisRecord: any) => {
              return searchResultRecord(thisRecord)
            })
          }

          </Grid>

        </AccordionDetails>
      </Accordion>
    )
  }

  return (
     <Box>

      <LinearProgress
        color="primary"
        sx={{visibility: isLoading ? 'visible' : 'hidden'}}
      />
      <Card sx={styles.containerCard}>
        <CardContent>
          <Stack spacing={3}>
          <form onSubmit={submitDatabaseSearch}>
            <TextField
              size="small"
              label="Search"
              variant="filled"
              InputProps={{
                disableUnderline: true,
                endAdornment: mutableSearchText ? (
                  <InputAdornment position="end">
                    {
                      <Tooltip title = "Search full database">
                        <IconButton
                          disabled={isLoading}
                          size="small"
                          type="submit"
                        >
                          <ArrowCircleUpIcon />
                        </IconButton>
                      </Tooltip>
                    }
                    <IconButton size="small" onClick={() => setMutableSearchText('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              value={mutableSearchText}
              onChange={(event) => setMutableSearchText(event.target.value)}
            />
            </form>

            <Divider/>

            <Box
               key={"Search Header"}
               sx={{
                 display: 'flex',
                 flexDirection: 'column',
                 alignItems: 'flex-start'
               }}
            >
              {
                searchResults.length > 0 &&
                <Typography
                  fontWeight={600}
                  sx={{mb: 2}}
                >
                  Select Objects
                </Typography>
              }
              {
                searchResults.length == 0 &&
                searchText !== '' &&
                !isLoading &&
                <Typography
                  fontWeight={600}

                >
                  No records found for search text: {searchText}
                </Typography>
              }

              {
                searchResults.length == 0 &&
                searchText === '' &&
                !isLoading &&
                <Typography
                  fontWeight={600}

                >
                  No search text specified
                </Typography>
              }

              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
              >
                {
                  searchResults.length > 0 &&
                  <Chip
                    key={"SelectAll"}
                    label={"Select All"}
                    clickable
                    color={filteredObjects.length === searchResults.length ? "primary" : "default"}
                    disabled={filteredObjects.length === searchResults.length}
                    onClick={() => handleSelectAll()}
                  />
                }
                {
                  searchResults.map((thisResult: GlobalSearchResultModel) => {
                    return (
                      <Chip
                        key={`chipObjectUuid:${thisResult.objectMetadata.Uuid_sys}`}
                        label={thisResult.objectMetadata.Label_sys}
                        clickable
                        color={filteredObjects.includes(thisResult.objectMetadata.Uuid_sys) ? "primary" : "default"}
                        onClick={() => handleSelectObject(thisResult.objectMetadata.Uuid_sys)}
                      />
                    )
                  })
                }
              </Stack>
            </Box>
            <Divider/>


           {/*BOX AS HEADER OF OBJECT NAME (CLICKABLE)*/}
           {
             searchResults.map((thisResult: GlobalSearchResultModel) => {
               return searchResultAccordion(thisResult)
             })
          }

          </Stack>
        </CardContent>
      </Card>
     </Box>

);
};
